import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Flame, Headphones, Loader2, TrendingUp } from 'lucide-react';
import AudioBiblePlayer from './AudioBiblePlayer';
import VerseReactionBar from './VerseReactionBar';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FeaturedVerseHero
 *
 * Fetches the #1 most-shared verse of the week and showcases it in large
 * typography with a one-tap "Listen & share now" action that opens the
 * Lumera Immersive Audio player (which itself surfaces the share sheet).
 *
 * Used on both the public landing page and the authenticated community feed.
 *
 * Props:
 *   variant: 'light' | 'dark' — controls contrast tuning.
 *   compact: boolean — tighter padding for in-feed usage.
 */
const FeaturedVerseHero = ({ variant = 'dark', compact = false }) => {
  const [verse, setVerse] = useState(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/shares/top?period=week&limit=1`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data) && data[0]) {
          setVerse({
            reference: data[0].verse_reference,
            text: data[0].verse_text,
            translation: data[0].translation || 'KJV',
          });
          setCount(data[0].count || 0);
        } else {
          setVerse(null);
        }
      } catch (_) {
        if (!cancelled) setVerse(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide entirely when there is nothing to feature yet — keeps the page clean.
  if (!loading && !verse) return null;

  const isDark = variant === 'dark';
  const sectionBg = isDark
    ? 'bg-gradient-to-br from-amber-950/40 via-slate-950 to-purple-950/40'
    : 'bg-gradient-to-br from-amber-50 via-white to-purple-50';
  const glow = isDark
    ? 'from-amber-500/20 via-transparent to-transparent'
    : 'from-amber-200/60 via-transparent to-transparent';
  const headingColor = isDark ? 'text-white' : 'text-slate-900';
  const subColor = isDark ? 'text-amber-200/80' : 'text-amber-700';
  const mutedColor = isDark ? 'text-slate-400' : 'text-slate-600';
  const verseColor = isDark ? 'text-white' : 'text-slate-900';

  return (
    <section
      className={`relative overflow-hidden rounded-3xl ${sectionBg} ${
        compact ? 'py-10 sm:py-14 px-6 sm:px-10' : 'py-20 sm:py-28 px-6 sm:px-10'
      }`}
      data-testid="featured-verse-hero"
    >
      {/* Radial glow */}
      <div
        className={`pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] ${glow}`}
      />

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Eyebrow badge */}
        <Badge
          variant="outline"
          className={`mb-6 gap-1.5 ${
            isDark
              ? 'bg-amber-500/10 border-amber-400/30 text-amber-200'
              : 'bg-amber-100 border-amber-300 text-amber-800'
          }`}
        >
          <Flame className="w-3.5 h-3.5" />
          Verse of the week
        </Badge>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className={`w-8 h-8 animate-spin ${mutedColor}`} />
          </div>
        ) : (
          <>
            <h2
              className={`font-serif font-bold italic leading-tight ${verseColor} ${
                compact
                  ? 'text-2xl sm:text-3xl md:text-4xl'
                  : 'text-3xl sm:text-5xl md:text-6xl'
              }`}
              data-testid="featured-verse-text"
            >
              &ldquo;{verse.text}&rdquo;
            </h2>

            <p
              className={`mt-6 font-semibold tracking-wide ${subColor} ${
                compact ? 'text-base' : 'text-lg sm:text-xl'
              }`}
              data-testid="featured-verse-reference"
            >
              — {verse.reference}
              {verse.translation ? (
                <span className={`ml-2 text-xs ${mutedColor}`}>({verse.translation})</span>
              ) : null}
            </p>

            {count > 0 && (
              <div
                className={`mt-3 inline-flex items-center gap-1.5 text-xs sm:text-sm ${mutedColor}`}
              >
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                <span className="font-semibold">{count}</span>
                <span>shares this week</span>
              </div>
            )}

            <div className="mt-8 flex justify-center">
              <Button
                size="lg"
                onClick={() => setPlayerOpen(true)}
                className="rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 shadow-xl shadow-amber-500/30"
                data-testid="featured-verse-listen-share-btn"
              >
                <Headphones className="w-5 h-5 mr-2" />
                Listen &amp; share now
              </Button>
            </div>

            {/* Daily reaction ritual — anonymous, one tap per device */}
            <div className="mt-6">
              <VerseReactionBar verse={verse} variant={variant} />
            </div>
          </>
        )}
      </div>

      {verse && (
        <AudioBiblePlayer
          isOpen={playerOpen}
          onClose={() => setPlayerOpen(false)}
          verse={verse}
        />
      )}
    </section>
  );
};

export default FeaturedVerseHero;
